import { useAuth } from '../contexts/AuthContext'
import styles from './NoAccessPage.module.css'

// handoff §5b / V4 "No access": an authenticated account with zero
// subscriptions. Echo the exact address and provider (most cases are a
// personal account used instead of the registered one), two actions, and
// disclose nothing else.

const PROVIDERS: Record<string, string> = {
  'google.com': 'Google',
  'microsoft.com': 'Microsoft',
  'apple.com': 'Apple',
  'facebook.com': 'Meta',
}

// Apple's "Hide my email" gives CONTROL a relay address in place of the person's email, which the API refuses
// (migration 140): it can never match the address their administrator registered.
const HIDDEN_EMAIL = /@privaterelay\.appleid\.com$/i

export function NoAccessPage() {
  const { user, signOut } = useAuth()
  const provider = PROVIDERS[user?.providerData[0]?.providerId ?? '']
  const hidden = HIDDEN_EMAIL.test(user?.email?.trim() ?? '')

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (err) {
      console.error('Sign-out failed:', err)
    }
  }

  return (
    <div className={styles.screen} data-screen-label="No access">
      <div className={styles.glowWarm} aria-hidden="true" />
      <div className={styles.glowSage} aria-hidden="true" />

      <div className={styles.brand}>
        <svg viewBox="0 0 149 151" width="24" className={styles.brandMark} aria-hidden="true">
          <path d="M74.49,2.91H3.63V117.08c17.9-1.59,44.14-9.49,62.73-28.46,4.33-4.29,7.18-7.62,10.22-13.01h-.73c-10.3,14.59-25.38,22.47-39.38,28.21-2.99-9.33-6.99-27.06,9.82-41.79,5.49-4.82,10.99-6.82,16.31-8.76,7.78-2.84,15.82-5.78,25.38-17.73l1.5-1.87,1.67,1.72c16.13,16.68,17.97,46.17,3.86,61.88-12.7,14.14-29.67,18.09-41.66,18.09-4.36,0-9.3-.98-11.77-1.86-12.26,5.94-24.93,10.14-37.94,11.86v19.28H74.49c39.14,0,70.87-31.73,70.87-70.87S113.63,2.91,74.49,2.91Z" />
        </svg>
        <span className={styles.brandName}>CONTROL</span>
      </div>

      <div className={styles.content}>
        <div className={styles.lockBadge} aria-hidden="true">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 9.9-1" />
          </svg>
        </div>

        {hidden ? (
          <>
            <h1 className={styles.title}>Apple hid your email</h1>
            <p className={styles.lead}>CONTROL needs your real email address to know who you are.</p>
          </>
        ) : (
          <>
            <h1 className={styles.title}>This account has no access</h1>
            <p className={styles.lead}>You signed in successfully, but CONTROL has nothing set up for this account yet.</p>
          </>
        )}

        <section className={styles.panel} aria-labelledby="no-access-signed-in-as">
          <div id="no-access-signed-in-as" className={styles.panelLabel}>
            You signed in as
          </div>
          <div className={styles.identity}>
            <span className={styles.mailBadge} aria-hidden="true">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m22 7-10 5L2 7" />
              </svg>
            </span>
            <span className={styles.email}>{user?.email || 'your account'}</span>
            {provider && <span className={styles.providerPill}>via {provider}</span>}
          </div>
          {hidden ? (
            <p className={styles.panelText}>
              You chose Hide My Email, so Apple sent a private relay address instead of yours. Sign in with Google or
              Microsoft instead, or stop using Sign in with Apple for CONTROL in your Apple Account settings and sign
              in again choosing Share My Email.
            </p>
          ) : (
            <p className={styles.panelText}>
              Make sure you sign in with the same email address you gave your administrator when your access was set
              up. A different address, even your own, is treated as a different person.
            </p>
          )}
        </section>

        <div className={styles.actions}>
          <button type="button" onClick={handleSignOut} className={styles.primary}>
            Try a different account
          </button>
          <a href="mailto:support@dataphyll.com" className={styles.secondary}>
            Email support@dataphyll.com
          </a>
        </div>

        <p className={styles.note}>
          Still stuck? Your administrator can confirm which address they registered. Dataphyll support cannot grant
          access on their behalf.
        </p>
      </div>
    </div>
  )
}
